import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowForward, CreditCard, OpenInNew, Refresh } from '@mui/icons-material';
import { API_CALLS_HINT } from '../constants/apiUsageCopy';
import { ApiCallsHint } from '../components/ApiCallsHint';
import { useQuery } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { UpgradePlansModal } from '../components/UpgradePlansModal';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useToast } from '../contexts/ToastContext';
import { PageContainer, PageHeader, StatusChip } from '../components/ui';
import { PlanAddonsCard } from '../components/PlanAddonsCard';
import { tokens } from '../theme/tokens';

const PRODUCTS_SYNCED_HINT =
  'Products in your sync selection (the largest list any integration pushes). The brand limit on a plan counts the brands in that selection, not the dealer brands on your Store page.';

interface BillingData {
  subscription: {
    id: string;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    trialEnd?: string | null;
  } | null;
  plan: {
    id: string;
    name: string;
    description?: string;
    price: number;
    currency?: string;
    interval: string;
    features?: string[];
    limits: {
      maxApiCalls: number;
      maxDataSync: number;
      productSyncCap?: boolean;
      maxIntegrations: number;
      maxUsers: number;
    };
  };
  usage: {
    apiCalls: number;
    dataSyncs: number;
    integrations: number;
    users: number;
  };
  invoices: Array<{
    id: string;
    amount: number;
    currency: string;
    status: string;
    date: string;
    invoiceUrl: string;
    pdfUrl: string;
    description: string;
  }>;
  paymentMethods: Array<{
    id: string;
    type: string;
    brand: string;
    last4: string;
    expiryMonth: number;
    expiryYear: number;
    isDefault: boolean;
  }>;
  organizationId: string | null;
  organizationName?: string | null;
  stripeCustomerId: string | null;
  hasAccessGrant?: boolean;
  /** Present for a tier, plan, or full-access grant. Features-only grants stay off `hasAccessGrant`. */
  accessGrant?: { fullAccess?: boolean } | null;
  tier?: string | null;
  setupFeeWaiver?: { campaign?: string | null; expiresAt?: string | null } | null;
  /** Additional-integration add-on line, present only when `integration_addon_billing` is on for the org. */
  integrationAddon?: {
    tier: string | null;
    addonAvailable: boolean;
    includedIntegrations: number;
    connectedIntegrations: number;
    addonQuantity: number;
    addonUnitPrice: number;
    addonCurrency: string;
    addonMonthly: number;
  } | null;
}

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatMoney = (amount: number, currency = 'USD') => {
  try {
    const fractionDigits = amount % 1 === 0 ? 0 : 2;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

/** Flattens nested parentheses in a plan name: "Impulse (Full (Grant))" becomes "Impulse (Full, Grant)". */
const formatPlanName = (name: string) => {
  let result = name;
  for (let i = 0; i < 5; i += 1) {
    const next = result.replace(/\(([^()]*)\(([^()]*)\)\s*\)/g, '($1, $2)');
    if (next === result) break;
    result = next;
  }
  return result.replace(/\s+/g, ' ').trim();
};

/** The page's white section card: hairline header row, body below. */
const SectionCard: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <Box
    sx={{
      bgcolor: tokens.surface.card,
      border: '1px solid',
      borderColor: tokens.line.default,
      borderRadius: `${tokens.radius.panel}px`,
      boxShadow: tokens.shadow.card,
      overflow: 'hidden',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 2, borderBottom: '1px solid', borderColor: tokens.line.hairline }}>
      <Typography sx={{ flex: 1, fontSize: 14.5, fontWeight: 700 }}>{title}</Typography>
      {action}
    </Box>
    {children}
  </Box>
);

interface UsageRow {
  label: string;
  used: number;
  /** null = unlimited */
  limit: number | null;
  /** Shown behind an info icon next to the label */
  hint?: string;
  /** Plain-language description of what the number counts, shown as a tooltip on the label */
  note?: string;
}

const UsageMeter: React.FC<UsageRow> = ({ label, used, limit, hint, note }) => {
  // null or a negative sentinel (-1) means unlimited: text only, no bar.
  const unlimited = limit === null || limit < 0;
  const limitReached = !unlimited && limit > 0 && used >= limit;
  const pct = unlimited ? 0 : Math.max(used > 0 ? 1 : 0, Math.min(100, (used / Math.max(limit, 1)) * 100));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <span title={note} style={note ? { cursor: 'help' } : undefined}>{label}</span>
          {hint && <ApiCallsHint label={`About ${label}`} size={14} />}
        </Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          {limitReached && <StatusChip label="Limit reached" tone="caution" />}
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, fontVariantNumeric: 'tabular-nums' }}>
            {used.toLocaleString('en-US')}
            {unlimited ? ' · Unlimited' : ` / ${limit.toLocaleString('en-US')}`}
          </Typography>
        </Box>
      </Box>
      {!unlimited && (
        <Box sx={{ height: 6, borderRadius: '4px', bgcolor: tokens.surface.tint, overflow: 'hidden' }}>
          <Box
            sx={{
              height: '100%',
              width: `${pct}%`,
              bgcolor: limitReached ? tokens.signal.caution.solid : tokens.accent.main,
              borderRadius: '4px',
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export const BillingManagementPage: React.FC = () => {
  const { isImpersonating, impersonatedOrg } = useImpersonation();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const { showToast } = useToast();

  const { data: billingData, isLoading, error, refetch, isFetching } = useQuery<BillingData>({
    queryKey: ['my-subscription', impersonatedOrg?.id || 'self'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/billing/my-subscription', { headers });
      if (!response.ok) throw new Error('Failed to fetch billing data');
      const result = await response.json();
      return result.data;
    },
  });

  const handleManagePayment = async () => {
    setPortalLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/billing/portal-session', {
        method: 'POST',
        headers,
        body: JSON.stringify({ returnUrl: window.location.href }),
      });
      if (!response.ok) throw new Error('Failed to create portal session');
      const result = await response.json();
      if (result.data?.url) window.location.href = result.data.url;
    } catch (err) {
      console.error('Error opening billing portal:', err);
      showToast('Failed to open billing portal. Please try again.', 'error');
    } finally {
      setPortalLoading(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer maxWidth={1180}>
        <PageHeader title="Billing" />
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={170} sx={{ borderRadius: `${tokens.radius.panel}px` }} />
          <Skeleton variant="rectangular" height={220} sx={{ borderRadius: `${tokens.radius.panel}px` }} />
          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: `${tokens.radius.panel}px` }} />
        </Stack>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer maxWidth={1180}>
        <PageHeader title="Billing" />
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load billing information. Please try again.</Alert>
        <Button variant="outlined" onClick={() => refetch()}>Retry</Button>
      </PageContainer>
    );
  }

  const plan = billingData?.plan;
  const usage = billingData?.usage || { apiCalls: 0, dataSyncs: 0, integrations: 0, users: 0 };
  const limits = plan?.limits || { maxApiCalls: 100, maxDataSync: -1, maxIntegrations: 0, maxUsers: 1 };
  const subscription = billingData?.subscription;
  const invoices = billingData?.invoices || [];
  const paymentMethods = billingData?.paymentMethods || [];
  const organizationName = billingData?.organizationName || (isImpersonating ? impersonatedOrg?.name : null) || null;
  const integrationAddon = billingData?.integrationAddon || null;
  const isFreeOfCharge = !plan || plan.price === 0;
  const isTrial = subscription?.status === 'trialing' || /trial/i.test(plan?.name || '');
  const productCapOn = limits.productSyncCap === true && limits.maxDataSync !== -1;
  // A tier or plan grant still leaves Change Plan available. Only a full grant
  // (tier `full`, or accessGrant.fullAccess) hides it and reads "Full access".
  const hasAccessGrant = billingData?.hasAccessGrant === true;
  const hasFullAccess =
    hasAccessGrant &&
    (billingData?.tier === 'full' || billingData?.accessGrant?.fullAccess === true);
  const grantTier = (billingData?.tier || '').trim().toLowerCase();
  const accessGrantChip = !hasAccessGrant
    ? null
    : hasFullAccess
      ? 'Full access'
      : grantTier
        ? `${grantTier} access grant`
        : 'Access grant';
  const planName = formatPlanName(plan?.name || 'Free Preview');

  const usageRows: UsageRow[] = [
    { label: 'API Calls', used: usage.apiCalls, limit: limits.maxApiCalls === -1 ? null : limits.maxApiCalls, hint: API_CALLS_HINT },
    { label: 'Products Synced', used: usage.dataSyncs, limit: productCapOn ? limits.maxDataSync : null, note: PRODUCTS_SYNCED_HINT },
    { label: 'Integrations', used: usage.integrations, limit: limits.maxIntegrations === -1 ? null : limits.maxIntegrations },
  ];

  const manageButton = (
    <Button
      size="small"
      onClick={handleManagePayment}
      disabled={portalLoading}
      endIcon={<OpenInNew sx={{ fontSize: 12 }} />}
      sx={{
        height: 32,
        px: 1.6,
        borderRadius: `${tokens.radius.small}px`,
        border: '1px solid',
        borderColor: tokens.accent.tintStrong,
        bgcolor: tokens.accent.tint,
        color: tokens.accent.hover,
        fontSize: 12.5,
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': { bgcolor: tokens.accent.tintStrong },
      }}
    >
      {portalLoading ? 'Opening…' : 'Manage'}
    </Button>
  );

  return (
    <PageContainer maxWidth={1180}>
      <PageHeader title="Billing" />
      {isImpersonating && !impersonatedOrg?.id && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You&apos;re in generic Store view — no customer is selected. Use Switch View → Select Organization to see that
          store&apos;s plan and usage.
        </Alert>
      )}

      <Stack spacing={2}>
        {/* Current plan hero */}
        <Box sx={{ bgcolor: tokens.accent.main, borderRadius: `${tokens.radius.panel}px`, boxShadow: tokens.shadow.card, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', px: 3, py: 2.75 }}>
            <Box sx={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: tokens.signal.info.border }}>
                {organizationName ? `${organizationName} · Current Plan` : 'Current Plan'}
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: tokens.ink.inverse, letterSpacing: '-0.01em' }}>
                {planName}
              </Typography>
              {accessGrantChip && (
                <Box
                  component="span"
                  sx={{
                    alignSelf: 'flex-start',
                    px: 1.25,
                    py: 0.375,
                    borderRadius: `${tokens.radius.pill}px`,
                    bgcolor: 'rgba(255,255,255,0.18)',
                    color: tokens.accent.tint,
                    fontSize: 11.5,
                    fontWeight: 600,
                  }}
                >
                  {accessGrantChip}
                </Box>
              )}
              <Typography sx={{ fontSize: 19, fontWeight: 700, color: tokens.ink.inverse }}>
                {isFreeOfCharge ? '$0' : formatMoney(plan.price, plan.currency || 'USD')}
                {!isFreeOfCharge && (
                  <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: tokens.signal.info.border }}>
                    {plan.interval === 'monthly' ? '/mo' : plan.interval === 'yearly' ? '/yr' : ''}
                  </Box>
                )}
              </Typography>
              {plan?.description && (
                <Typography sx={{ fontSize: 13, color: tokens.accent.tint, maxWidth: 560 }}>{plan.description}</Typography>
              )}
            </Box>
            <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1.25 }}>
              {!hasFullAccess && (
                <Button
                  onClick={() => setUpgradeModalOpen(true)}
                  endIcon={<ArrowForward sx={{ fontSize: 15 }} />}
                  sx={{
                    height: 38,
                    px: 2,
                    bgcolor: tokens.ink.inverse,
                    color: tokens.accent.hover,
                    borderRadius: `${tokens.radius.control}px`,
                    fontSize: 13.5,
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': { bgcolor: tokens.accent.tint },
                  }}
                >
                  {isFreeOfCharge || isTrial ? 'Upgrade Plan' : 'Change Plan'}
                </Button>
              )}
              {(subscription?.currentPeriodEnd || subscription?.trialEnd) && (
                <Typography sx={{ fontSize: 12, color: tokens.signal.info.border }}>
                  {isTrial && subscription?.trialEnd
                    ? `Trial ends ${formatDate(subscription.trialEnd)}`
                    : `${subscription?.cancelAtPeriodEnd ? 'Cancels' : 'Renews'} ${formatDate(subscription?.currentPeriodEnd)}`}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Usage */}
        <SectionCard
          title="Usage This Cycle"
          action={
            <Tooltip title="Refresh usage">
              <IconButton size="small" onClick={() => refetch()} disabled={isFetching} sx={{ color: tokens.ink.faint }}>
                <Refresh sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, px: 2.5, py: 2.25 }}>
            {usageRows.map(row => (
              <UsageMeter key={row.label} {...row} />
            ))}
            {integrationAddon && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  pt: 1.75,
                  borderTop: '1px solid',
                  borderColor: tokens.line.faint,
                }}
              >
                {integrationAddon.addonQuantity > 0 ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.25 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>Additional integrations</Typography>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tokens.ink.body, fontVariantNumeric: 'tabular-nums' }}>
                        {formatMoney(integrationAddon.addonMonthly, integrationAddon.addonCurrency)}/mo
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>
                      {integrationAddon.addonQuantity} × {formatMoney(integrationAddon.addonUnitPrice, integrationAddon.addonCurrency)} / month
                      {integrationAddon.includedIntegrations >= 0 &&
                        ` — the first ${integrationAddon.includedIntegrations} are included`}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
                    Included integrations: used {integrationAddon.connectedIntegrations} of{' '}
                    {integrationAddon.includedIntegrations === -1 ? 'unlimited' : integrationAddon.includedIntegrations}
                    {!integrationAddon.addonAvailable &&
                      integrationAddon.includedIntegrations !== -1 &&
                      ' — additional integrations are available on Pro and above'}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </SectionCard>

        {/* A la carte add-ons */}
        <PlanAddonsCard />

        {/* Payment method */}
        <SectionCard title="Payment Method" action={manageButton}>
          {paymentMethods.length > 0 ? (
            <Box>
              {paymentMethods.map(pm => (
                <Box key={pm.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.75, px: 2.5, py: 2.25 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 44,
                      height: 32,
                      borderRadius: `${tokens.radius.chip}px`,
                      bgcolor: tokens.surface.header,
                      color: tokens.ink.inverse,
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                    }}
                  >
                    {pm.brand}
                  </Box>
                  <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>
                    •••• {pm.last4}{' '}
                    <Box component="span" sx={{ fontWeight: 400, color: tokens.ink.muted }}>
                      exp {String(pm.expiryMonth).padStart(2, '0')}/{String(pm.expiryYear).slice(-2)}
                    </Box>
                  </Typography>
                  {pm.isDefault && paymentMethods.length > 1 && (
                    <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>Default</Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25, px: 2.5, py: 4.5 }}>
              <CreditCard sx={{ fontSize: 30, color: tokens.ink.disabled }} />
              <Typography sx={{ fontSize: 13.5, color: tokens.ink.muted }}>No payment method on file</Typography>
              <Button
                variant="text"
                onClick={handleManagePayment}
                disabled={portalLoading}
                sx={{ fontSize: 13, fontWeight: 700, textTransform: 'none', color: tokens.accent.main }}
              >
                Add Payment Method
              </Button>
            </Box>
          )}
        </SectionCard>

        {/* Order history */}
        <SectionCard title="Order History">
          {invoices.length > 0 ? (
            <Box>
              {invoices.map(invoice => (
                <Box
                  key={invoice.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.75, px: 2.5, py: 1.6, borderTop: '1px solid', borderColor: tokens.line.faint }}
                >
                  <Typography sx={{ flex: 1, fontSize: 13, color: tokens.ink.body }}>{formatDate(invoice.date)}</Typography>
                  <Typography sx={{ fontSize: 13, color: tokens.ink.muted }}>{invoice.description}</Typography>
                  <Typography sx={{ width: 90, textAlign: 'right', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(invoice.amount, invoice.currency?.toUpperCase() || 'USD')}
                  </Typography>
                  {(invoice.pdfUrl || invoice.invoiceUrl) && (
                    <Button
                      size="small"
                      href={invoice.pdfUrl || invoice.invoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontSize: 12.5, fontWeight: 600, textTransform: 'none', minWidth: 0, px: 0.5 }}
                    >
                      Download
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ px: 2.5, py: 3.75, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13.5, color: tokens.ink.faint }}>No invoices yet</Typography>
            </Box>
          )}
        </SectionCard>
      </Stack>

      <UpgradePlansModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        currentPlanId={plan?.id || 'free-preview'}
        organizationId={billingData?.organizationId || undefined}
        setupFeeWaiver={billingData?.setupFeeWaiver || null}
      />
    </PageContainer>
  );
};
